<template>
  <div
    v-if="ready"
  >
    <div v-if="!redactCity && !redactMayor && !redactState">
      <div class="message">
        <p class="message-body">
          Select one or more forms above
        </p>
      </div>
    </div>
    <div v-else>
      <div class="columns is-multiline">
        <div class="column">
          <p><strong>Select the fields you would like to redact:</strong></p><br>
          <div class="message">
            <div class="message-header">
              Filer Information:
            </div>
            <div class="message-body">
              <ul
                class="redact-lists"
              >
                <template v-for="fieldKey in Object.keys(filing)">
                  <li
                    v-if="filerInfoFields.includes(fieldKey)"
                    :key="fieldKey"
                  >
                    <checkbox
                      v-model="redactFields.filing"
                      :value="fieldKey"
                      class="is-inline-block"
                    /><strong>{{ makeLabelFromKey(fieldKey) }}:</strong> {{ getControlValueLabel(fieldKey, filing[fieldKey]) }}
                  </li>
                </template>
              </ul>
            </div>
          </div>
          <template v-if="familyMembers.length > 0">
            <div class="message">
              <div class="message-header">
                Family Members:
              </div>
              <div class="message-body">
                <div class="small">
                  The relationship <b>type</b> cannot be redacted because the forms need this information to display it correctly. It's shown here for reference only.
                </div><br>
                <ul
                  v-for="(familyMember, index) in familyMembers"
                  :key="familyMember.id"
                  class="redact-lists"
                >
                  <li>
                    <h5 class="is-size-5">
                      Family Member {{ index + 1 }}
                    </h5>
                    <ul
                      class="redact-lists"
                    >
                      <template v-for="fieldKey in Object.keys(familyMember)">
                        <li
                          v-if="familyMembersFields.includes(fieldKey)"
                          :key="fieldKey"
                        >
                          <checkbox
                            v-model="redactFields.familyMembers"
                            :value="JSON.stringify({ id: familyMember.id, field: fieldKey })"
                            class="is-inline-block"
                            :disabled="fieldKey === 'type' ? true : false"
                          /><strong>{{ makeLabelFromKey(fieldKey) }}:</strong> {{ getControlValueLabel(`fam-${fieldKey}`, familyMember[fieldKey]) }}
                        </li>
                      </template>
                    </ul>
                  </li>
                </ul>
              </div>
            </div>
          </template>
          <template v-if="positions.length > 0">
            <div class="message">
              <div class="message-header">
                Filer Positions:
              </div>
              <div class="message-body">
                <ul
                  v-for="(position, index) in positions"
                  :key="position.id"
                  class="redact-lists"
                >
                  <li>
                    <h5 class="is-size-5">
                      Position {{ index + 1 }}
                    </h5>
                    <ul
                      class="redact-lists"
                    >
                      <template v-for="fieldKey in Object.keys(position)">
                        <li
                          v-if="positionFields.includes(fieldKey)"
                          :key="fieldKey"
                        >
                          <checkbox
                            v-model="redactFields.positions"
                            :value="JSON.stringify({ id: position.id, field: fieldKey })"
                            class="is-inline-block"
                          /><strong>{{ makeLabelFromKey(fieldKey) }}:</strong> {{ getControlValueLabel(fieldKey, position[fieldKey]) }}
                        </li>
                      </template>
                    </ul>
                  </li>
                </ul>
              </div>
            </div>
          </template>
          <template v-if="incomeSources.length > 0">
            <div class="message">
              <div class="message-header">
                Income Sources:
              </div>
              <div class="message-body">
                <ul
                  v-for="(incomeSource, index) in incomeSources"
                  :key="incomeSource.id"
                  class="redact-lists"
                >
                  <li>
                    <h5 class="is-size-5">
                      Income Source {{ index + 1 }}
                    </h5>
                    <ul
                      class="redact-lists"
                    >
                      <template v-for="fieldKey in Object.keys(incomeSource)">
                        <li
                          v-if="incomeSourcesFields.includes(fieldKey)"
                          :key="fieldKey"
                        >
                          <checkbox
                            v-model="redactFields.incomeSources"
                            :value="JSON.stringify({ id: incomeSource.id, field: fieldKey })"
                            class="is-inline-block"
                          /><strong>{{ makeLabelFromKey(fieldKey) }}:</strong> {{ getControlValueLabel(fieldKey, incomeSource[fieldKey]) }}
                        </li>
                      </template>
                    </ul>
                  </li>
                </ul>
              </div>
            </div>
          </template>
          <div class="has-text-centered">
            <button
              id="modal-redact-btn"
              type="button"
              class="button is-primary"
              :class="isSaving ? 'is-loading' : ''"
              @click.prevent="redact"
            >
              Redact
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import Checkbox from '@/components/phila-ui/CheckboxAdv';
import { pdfData } from '@/mixins/pdfData';

export default {
  name: 'ElectronicRedactions',
  components: {
    Checkbox,
  },
  mixins: [
    pdfData,
  ],
  props: {
    filingId: {
      type: Number,
      required: true,
    },
    cityOfficialsCategory: {
      type: String,
      required: true,
    },
    stateOfficialsCategory: {
      type: String,
      required: true,
    },
    mayorOfficialsCategory: {
      type: String,
      required: true,
    },
    redactCity: {
      type: Boolean,
      required: true,
    },
    redactState: {
      type: Boolean,
      required: true,
    },
    redactMayor: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      isSaving: false,
      isDeletingCity: false,
      isDeletingState: false,
      isDeletingMayor: false,
      ready: false,
      redactFields: {
        filing: [],
        incomeSources: [],
        positions: [],
        familyMembers: [],
      },
      filerInfoFields: [
        "homeStreetAddress",
        "homeCity",
        "homeState",
        "homeZip",
        "homePhone",
        "privateBusinessStreetAddress",
        "privateBusinessCity",
        "privateBusinessState",
        "privateBusinessZip",
      ],
      positionFields: [
        "publicPosition",
        "affiliationStatus",
        "profession",
        "workStreetAddress",
        "workCity",
        "workState",
        "workZip",
        "workPhone",
      ],
      incomeSourcesFields: [
        "streetAddress",
        "city",
        "state",
        "zip",
        "natureOfIncome",
        "name",
        "amount",
        "natureofbusiness",
        "type",
        "person",
        "incomeGain",
      ],
      familyMembersFields:  [
        'type',
        'name',
        'occupation',
      ],
    };
  },
  computed: {
    filing () {
      return this.$store.state.filing.filing;
    },
    positions () {
      return this.$store.state.filing.currentFilingPositions;
    },
    incomeSources () {
      return this.$store.state.incomeSources.incomeSources;
    },
    familyMembers () {
      return this.$store.state.family.familyMembers;
    },
    familyRelationships () {
      return this.$store.getters[ 'admin/activeControlValues' ]('familymember');
    },
    states () {
      return this.$store.getters[ 'admin/activeControlValues' ]('state');
    },
    incomeTypes () {
      return this.$store.getters[ 'admin/activeControlValues' ]('incometype');
    },
    affiliationStatuses () {
      return this.$store.getters[ 'admin/activeControlValues' ]('affiliationstatus');
    },
  },
  async created () {
    await this.initialFetch();
    this.ready = true;
  },
  methods: {
    async initialFetch () {
      let promises = [];
      await this.$store.commit('filing/setCurrentFilingId', this.filingId);
      promises.push(await this.$store.dispatch('filing/fetchFiling'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'agency'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'state'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'affiliationstatus'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'familymember'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'incometype'));
      promises.push(this.$store.dispatch('filing/fetchCurrentFilingPositions'));
      promises.push(this.$store.dispatch('family/fetchFamilyMembers', this.$store.state.user.tempId));
      promises.push(this.$store.dispatch('incomeSources/fetchIncomeSources', this.$store.state.user.tempId));
      await Promise.all(promises);
    },
    makeLabelFromKey (key) {
      let label = key.charAt(0).toUpperCase() + key.substring(1);
      return label.match(/[A-Z][a-z]+|[0-9]+/g).join(" ");
    },
    getControlValueLabel (key, value) {
      if (key === 'person') {
        let person = this.familyRelationships.find(relationship => relationship.id === parseInt(value));
        if (person) {
          return person.description;
        }
      } else if ([ "state", "workState", "homeState", "privateBusinessState" ].includes(key)) {
        let state = this.states.find(states => states.id === parseInt(value));
        if (state) {
          return state.description;
        }

      } else if (key === 'type') {
        let type = this.incomeTypes.find(type => type.id === parseInt(value));
        if (type) {
          return type.description;
        }
      } else if (key === 'affiliationStatus') {
        let affiliationStatus = this.affiliationStatuses.find(affiliationStatus => affiliationStatus.id === parseInt(value));
        if (affiliationStatus) {
          return affiliationStatus.description;
        }
      } else if (key === 'fam-type') {
        let type = this.familyRelationships.find(type => type.id === parseInt(value));
        if (type) {
          return type.description;
        }
      }
      return value;
    },
    async redact () {

      this.isSaving = true;
      let cityFormData = null;
      let stateFormData = null;
      let mayorFormData = null;

      if (!this.redactCity && !this.redactState && !this.redactMayor) {
        this.isSaving = false;
        return this.$warning('Please select a form type and corresponding Official\'s Category');
      }

      let redactFields = this.cloneObj(this.redactFields);

      Object.keys(redactFields).forEach(section => {
        if (section !== 'filing') {
          redactFields[section].forEach((field, fieldIndex) => {
            redactFields[section][fieldIndex] = JSON.parse(redactFields[section][fieldIndex]);
          });
        }
      });

      if (this.redactCity) {
        if (!this.cityOfficialsCategory) {
          this.isSaving = false;
          return this.$warning('Please select a Official\'s Category');
        }
        cityFormData = await this.pdf_getPdfData('City', this.filing.id, false, redactFields);
        cityFormData.officialsCategory = this.cityOfficialsCategory;
      }

      if (this.redactState) {
        if (!this.stateOfficialsCategory) {
          this.isSaving = false;
          return this.$warning('Please select a Official\'s Category');
        }
        stateFormData = await this.pdf_getPdfData('State', this.filing.id, false, redactFields);
        stateFormData.officialsCategory = this.stateOfficialsCategory;
      }

      if (this.redactMayor) {
        if (!this.mayorOfficialsCategory) {
          this.isSaving = false;
          return this.$warning('Please select a Official\'s Category');
        }
        mayorFormData = await this.pdf_getPdfData('Mayor', this.filing.id, false, redactFields);
        mayorFormData.officialsCategory = this.mayorOfficialsCategory;
      }


      if (!redactFields.filing.length > 0 && !redactFields.incomeSources.length > 0 && !redactFields.positions.length > 0 && !redactFields.familyMembers.length > 0) {
        this.isSaving = false;
        return this.$warning('Please select at least one field to be redacted.');
      }

      let pdfPromises = [];

      if (cityFormData) {
        pdfPromises.push(this.$store.dispatch('filing/onlineRedaction', cityFormData));
      }
      if (stateFormData) {
        pdfPromises.push(this.$store.dispatch('filing/onlineRedaction', stateFormData));
      }
      if (mayorFormData) {
        pdfPromises.push(this.$store.dispatch('filing/onlineRedaction', mayorFormData));
      }

      Promise.all(pdfPromises).then(async() => {
        this.$success('Redaction completed.');
        this.isSaving = false;
        this.$emit('redactionCompleted', true);
      });

    },
  },
};
</script>
<style lang="scss" scoped>
  .redact-lists {
    li {
      margin: 3px 0;
      ul.redact-lists {
        margin-bottom: 20px;
      }
    }
  }
</style>
